import {
  GoogleMap,
  InfoWindow,
  Marker,
  Polyline,
  Circle,
  Polygon,
} from '@react-google-maps/api';
import moment from 'moment-timezone';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import checkedIn from '../../../../assets/img/icons/checked-in.svg';
import checkedOut from '../../../../assets/img/icons/checked-out.svg';
import clockedIn from '../../../../assets/img/icons/clocked-in.svg';
import clockedOut from '../../../../assets/img/icons/clocked-out.svg';
import routeEnd from '../../../../assets/img/icons/route-end.svg';
import routeMarker from '../../../../assets/img/icons/route-marker.svg';
import routeStart from '../../../../assets/img/icons/route-start.svg';
import focusIcon from '../../../../assets/img/icons/camera.svg';
import {
  CommonFlex,
  CommonText,
  DividerLine,
  LabelWithTextSection,
} from '../../../../styledComponents/common';
import { MarkerInfoContainer, TimeInfoDiv } from '../routeMapStyles';

const options = {
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  disableDoubleClickZoom: true,
  gestureHandling: 'cooperative',
  mapId: process.env.REACT_APP_JOB_SITES_MAP_ID,
};

const containerStyle = {
  width: '100%',
  height: '80vh',
  // borderLeft: "1px solid #e4e7eb",
  borderRadius: '10px',
  // marginTop: "10px",
};
const center = {
  lat: 21.424090674479555,
  lng: 39.822141261515604,
};

const routeColors = ['#6fb1fc', '#ff9999', '#82e0aa', '#b19cd9'];


const ClockInOutMarker = props => {
  const { marker, activeClockInOutMarker, setActiveClockInOutMarker } = props;

  const { t } = useTranslation();

  const handleMarkerClick = () => {
    if (
      !activeClockInOutMarker ||
      (activeClockInOutMarker && activeClockInOutMarker !== marker.timestamp)
    ) {
      setActiveClockInOutMarker(marker.timestamp);
    } else {
      setActiveClockInOutMarker(null);
    }
  };

  return (
    <Marker
      key={marker.timestamp}
      icon={{
        url: marker.event === 'clock_in' ? clockedIn : clockedOut,
        anchor: new window.google.maps.Point(15, 15),
      }}
      position={marker.location}
      // onClick={() => handleMarkerClick()}
      onMouseOver={() => handleMarkerClick()}
      onMouseOut={() => setActiveClockInOutMarker(null)}
    >
      {activeClockInOutMarker &&
        activeClockInOutMarker === marker.timestamp && (
          <InfoWindow onCloseClick={() => setActiveClockInOutMarker(null)}>
            <MarkerInfoContainer>
              <LabelWithTextSection padding="10px 15px 15px" gap="8px">
                <CommonText $label fontSize="12px" fontWeight="500">
                  {t(marker.event)}
                </CommonText>
                <TimeInfoDiv>
                  {moment.unix(marker.timestamp).format('h:mm A')}
                </TimeInfoDiv>
              </LabelWithTextSection>
            </MarkerInfoContainer>
          </InfoWindow>
        )}
    </Marker>
  );
};

const TaskMarker = props => {
  const { marker, activeTaskMarker, setActiveTaskMarker } = props;

  const { t } = useTranslation();

  const handleMarkerClick = () => {
    if (
      !activeTaskMarker ||
      (activeTaskMarker && activeTaskMarker !== marker.timestamp)
    ) {
      setActiveTaskMarker(marker.timestamp);
    } else {
      setActiveTaskMarker(null);
    }
  };

  return (
    <Marker
      key={marker.timestamp}
      icon={{
        url: marker.event === 'checkin' ? checkedIn : checkedOut,
        anchor: new window.google.maps.Point(15, 1),
      }}
      position={marker.location}
      // onClick={() => handleMarkerClick()}
      onMouseOver={() => handleMarkerClick()}
      onMouseOut={() => setActiveTaskMarker(null)}
    >
      {activeTaskMarker && activeTaskMarker === marker.timestamp && (
        <InfoWindow onCloseClick={() => setActiveTaskMarker(null)}>
          <MarkerInfoContainer>
            <LabelWithTextSection padding="10px 15px 15px" gap="8px">
              <CommonText $label fontSize="12px" fontWeight="500">
                {marker.event === 'checkin'
                  ? t('checked_in')
                  : t('checked_out')}
              </CommonText>
              <TimeInfoDiv>
                {moment.unix(marker.timestamp).format('h:mm A')}
              </TimeInfoDiv>
            </LabelWithTextSection>
          </MarkerInfoContainer>
        </InfoWindow>
      )}
    </Marker>
  );
};

const RouteMapMarker = props => {
  const { marker, timesheet, activeMarkerId, setActiveMarkerId } = props;

  const handleMarkerClick = () => {
    if (!activeMarkerId || (activeMarkerId && activeMarkerId !== marker.id)) {
      setActiveMarkerId(marker.id);
    } else {
      setActiveMarkerId(null);
    }
  };

  return (
    <Marker
      key={marker.id}
      // icon={getMarkerIcon(marker)}
      icon={
        marker.icon
          ? {
            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            ...marker.icon,
          }
          : null
      }
      position={marker.location}
      onClick={() => handleMarkerClick()}
    >
      {activeMarkerId && activeMarkerId === marker.id && (
        <InfoWindow onCloseClick={() => setActiveMarkerId(null)}>
          <MarkerInfoContainer>
            <LabelWithTextSection padding="10px 15px 15px" gap="8px">
              <CommonText $label fontSize="12px" fontWeight="500">
                {moment.unix(marker.timestamp).format('ddd, MMMM DD, YYYY')}
              </CommonText>
              <TimeInfoDiv>
                {moment.unix(marker.timestamp).format('h:mm A')}
              </TimeInfoDiv>
            </LabelWithTextSection>
          </MarkerInfoContainer>
        </InfoWindow>
      )}
    </Marker>
  );
};

const polygonOptions = {
  fillColor: '#FF5252',
  fillOpacity: 0.05,
  strokeColor: '#FF5252',
  strokeWeight: 3,
  clickable: true,
  editable: false,
};

const circleOptions = {
  fillColor: '#4CAF50',
  fillOpacity: 0.05,
  strokeColor: '#4CAF50',
  strokeWeight: 3,
  clickable: true,
  editable: false,
};

const GeofenceSidePanel = ({
  circularGeofenceLocations,
  polygonGeofenceLocations,
  onFocusGeofence,
  focusedGeofence
}) => {
  const { t } = useTranslation();

  // Combine both types of geofences
  const allGeofences = [
    ...(circularGeofenceLocations || []).map(loc => ({ ...loc, displayType: 'circular' })),
    ...(polygonGeofenceLocations || []).map(loc => ({ ...loc, displayType: 'polygonal' }))
  ];

  if (!allGeofences.length) return null;

  return (
    <div style={{
      position: 'absolute',
      top: '20px',
      right: '20px',
      width: '250px',
      maxHeight: '80%',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      zIndex: 1000,
      overflow: 'auto',
      padding: '15px'
    }}>
      <CommonText fontSize="16px" fontWeight="600" marginBottom="15px">
        {t('Geofence Locations')}
      </CommonText>
      <DividerLine marginBottom="15px" />

      {allGeofences.map((geofence, index) => {
        const isSelected = focusedGeofence && focusedGeofence.id === geofence.id;

        return (
          <div
            key={`geofence-${index}`}
            onClick={() => onFocusGeofence(geofence)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px',
              borderBottom: index < allGeofences.length - 1 ? '1px solid #eee' : 'none',
              backgroundColor: isSelected ? '#f0f7ff' : 'transparent',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              marginBottom: '5px',
              marginTop: '10px'
            }}
          >
            <CommonFlex direction="column" gap="4px">
              <CommonText fontSize="14px" fontWeight="500">
                {geofence.name}
              </CommonText>
            </CommonFlex>
          </div>
        );
      })}
    </div>
  );
};

const RouteMapGoogleMap = props => {
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [activeClockInOutMarker, setActiveClockInOutMarker] = useState(null);
  const [activeTaskMarker, setActiveTaskMarker] = useState(null);
  const [focusedGeofence, setFocusedGeofence] = useState(null);

  const {
    markers,
    // linePoints,
    routes,
    setMap,
    map,
    // selectedTimesheet,
    taskMarkers,
    clockInOutMarkers,
    geofenceLocations,
  } = props;

  const [circularGeofenceLocations, setCircularGeofenceLocations] = useState([]);
  const [polygonGeofenceLocations, setPolygonGeofenceLocations] = useState([]);

  // Filter Geofence Locations Based on Type
  useEffect(() => {
    if (geofenceLocations && geofenceLocations.length > 0) {
      setCircularGeofenceLocations(geofenceLocations.filter(location => location.type === 'circular'));
      setPolygonGeofenceLocations(geofenceLocations.filter(location => location.type === 'polygonal'));
    }

    if (geofenceLocations && geofenceLocations.length === 0) {
      setCircularGeofenceLocations([]);
      setPolygonGeofenceLocations([]);
    }
  }, [geofenceLocations]);

  useMemo(() => {
    if (map && routes && routes.length) {
      const bounds = new window.google.maps.LatLngBounds();
      routes.forEach(route => {
        let points = JSON.parse(JSON.stringify(route.points));
        points.forEach(line => {
          const position = new window.google.maps.LatLng(
            parseFloat(line.lat),
            parseFloat(line.lng),
          );
          bounds.extend(position);
        });
      });

      map.fitBounds(bounds);
    }
  }, [map, routes]);

  const renderCircularGeofenceLocations = () => {
    if (!circularGeofenceLocations || !circularGeofenceLocations.length) return null;

    return circularGeofenceLocations.map((location, index) => {
      // Check if we have enough data to render the circle
      // We need either latitude/longitude or an address that can be geocoded
      if (!location.latitude && !location.longitude && !location.address) {
        console.warn(`Circular geofence ${location.id} has insufficient data for rendering`);
        return null;
      }

      // If we have latitude and longitude, use them directly
      let center;
      if (location.latitude && location.longitude) {
        center = {
          lat: parseFloat(location.latitude),
          lng: parseFloat(location.longitude),
        };
      } else if (location.address) {
        // If we only have an address, we would need to geocode it
        // For now, we'll use a placeholder and log a warning
        console.warn(`Circular geofence ${location.id} has address but no coordinates. Geocoding would be needed.`);
        return null; // Don't render if we can't get coordinates
      } else {
        return null; // Don't render if we don't have enough data
      }

      // Check if we have a valid radius
      const radius = parseFloat(location.radius);
      if (isNaN(radius) || radius <= 0) {
        console.warn(`Circular geofence ${location.id} has invalid radius: ${location.radius}`);
        return null;
      }

      return (
        <Circle
          key={`circular-${index}`}
          center={center}
          radius={radius}
          options={circleOptions}
        />
      );
    }).filter(Boolean); // Filter out null values
  };

  const renderPolygonGeofenceLocations = () => {
    if (!polygonGeofenceLocations || !polygonGeofenceLocations.length) return null;

    return polygonGeofenceLocations.map((location, index) => {
      // Convert vertices array to the format expected by the Polygon component
      const paths = location.vertices.map(vertex => ({
        lat: vertex[0],
        lng: vertex[1]
      }));

      return (
        <Polygon
          key={`polygonal-${index}`}
          paths={paths}
          options={polygonOptions}
        />
      );
    });
  };

  // Function to focus on a specific geofence
  const handleFocusGeofence = (geofence) => {
    setFocusedGeofence(geofence);

    if (map) {
      if (geofence.displayType === 'circular') {
        // For circular geofences, center on the location and zoom to show the radius
        if (geofence.latitude && geofence.longitude) {
          const center = {
            lat: parseFloat(geofence.latitude),
            lng: parseFloat(geofence.longitude),
          };

          map.panTo(center);

          // Significantly increase zoom level for circular geofences (approximately 3x)
          const radius = parseFloat(geofence.radius);
          // Use a much higher base zoom level and smaller divisor for maximum zoom
          const zoomLevel = Math.floor(20 - Math.log2(radius / 20));
          map.setZoom(Math.min(Math.max(zoomLevel, 18), 22));
        }
      } else if (geofence.displayType === 'polygonal') {
        // For polygonal geofences, fit bounds to the polygon
        if (geofence.vertices && geofence.vertices.length > 0) {
          const bounds = new window.google.maps.LatLngBounds();

          geofence.vertices.forEach(vertex => {
            bounds.extend(new window.google.maps.LatLng(vertex[0], vertex[1]));
          });

          map.fitBounds(bounds, { padding: 50 });
        }
      }
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <GoogleMap
        options={{
          ...options,
          zoomControlOptions: {
            position: window.google.maps.ControlPosition.LEFT_BOTTOM,
          },
        }}
        mapContainerStyle={containerStyle}
        center={center}
        zoom={13}
        onLoad={map => setMap(map)}
        clickableIcons={false}
      >
        {renderCircularGeofenceLocations()}
        {renderPolygonGeofenceLocations()}
        {markers && markers.length
          ? markers.map(marker => {
            return (
              <RouteMapMarker
                marker={marker}
                // timesheet={selectedTimesheet}
                activeMarkerId={activeMarkerId}
                setActiveMarkerId={setActiveMarkerId}
              />
            );
          })
          : null}
        {clockInOutMarkers && clockInOutMarkers.length
          ? clockInOutMarkers.map(marker => {
            return (
              <ClockInOutMarker
                marker={marker}
                activeClockInOutMarker={activeClockInOutMarker}
                setActiveClockInOutMarker={setActiveClockInOutMarker}
              />
            );
          })
          : null}
        {taskMarkers && taskMarkers.length
          ? taskMarkers.map(marker => {
            return (
              <TaskMarker
                marker={marker}
                activeTaskMarker={activeTaskMarker}
                setActiveTaskMarker={setActiveTaskMarker}
              />
            );
          })
          : null}
        {/* {linePoints && linePoints.length ? (
          <Polyline
            path={linePoints}
            options={{
              geodesic: false,
              strokeColor: '#4d94fb',
              strokeOpacity: 1.0,
              strokeWeight: 6,
            }}
          />
        ) : null} */}
        {routes && routes.length > 0
          ? routes.map((route, index) => {
            if (route.points && route.points.length > 0) {
              return (
                <Polyline
                  key={index}
                  path={route.points}
                  options={{
                    geodesic: false,
                    strokeColor:
                      routeColors[index % routeColors.length] || routeColors[0],
                    strokeOpacity: 1.0,
                    strokeWeight: 6,
                  }}
                />
              );
            }
            return null;
          })
          : null}
      </GoogleMap>

      <GeofenceSidePanel
        circularGeofenceLocations={circularGeofenceLocations}
        polygonGeofenceLocations={polygonGeofenceLocations}
        onFocusGeofence={handleFocusGeofence}
        focusedGeofence={focusedGeofence}
      />
    </div>
  );
};

export default RouteMapGoogleMap;
